import arcadeFont from '../assets/fonts/press-start-2p.woff2';
import brandmark from '../assets/brand/brandmark.webp';
import dogData from '../data/dogs.json';
import leaderboard from './Leaderboard';
import {boardResult} from '../libs/startState.js';
import music from './Music';
import sfx from './MenuSfx';
import StartForm, {START_FORM_STYLES} from './StartForm';
import {UI} from '../libs/palette';
import {requestGameFullscreen} from './RotatePrompt';
import {NUDGE_STYLES, nudgeMarkup, bindNudge} from './SidewaysNudge';

import portraitFrames from '../data/portraits.json';
import summerStrip from '../assets/portraits/summer-strip.png';
import winstonStrip from '../assets/portraits/winston-strip.png';
import kevinStrip from '../assets/portraits/kevin-strip.png';
import picklesStrip from '../assets/portraits/pickles-strip.png';

// Each portrait is a strip of [idle, blink, tongue] frames, made by
// tools/animate_portrait.py and played by the portrait-idle keyframes below.
const PORTRAIT_STRIPS = {
  summer: summerStrip,
  winston: winstonStrip,
  kevin: kevinStrip,
  pickles: picklesStrip
};

// Where in the loop each dog starts, so the four never blink in unison.
const LOOP_SECONDS = 4.5;
const PORTRAIT_OFFSET = {summer: 0, winston: 1.1, kevin: 2.3, pickles: 3.4};

// Stats load in on first open (ms): each card starts a beat after the last,
// rows follow each other, and a row's bars fill one segment at a time.
const STAT_TIMING = {start: 300, card: 90, row: 380, bar: 70};

const STORAGE_KEY = 'receipthunt.player';
// The board waits this long for a score an earlier page couldn't send
// (Leaderboard.watchOutbox), so a player back from PLAY AGAIN sees their
// score on it; a slower one refreshes the board when it lands.
const OUTBOX_WAIT_MS = 2500;
const EMAIL_KEY = 'receipthunt.email';

// Palette and structure taken from the "V3 - Riso Pop" frame in pen/ReceiptHunt.pen
const {INK, LIME, PAPER, PICKED_BG, SLATE, MIST} = UI;

/*
 * The start screen fits one window. A wide window under 1000px tall (a laptop
 * with tabs and a bookmarks bar, a tablet on its side) keeps the four full
 * cards, but the spacing, the masthead and the form shrink with the window
 * height (vh clamps) and the dog portraits take whatever height is left, so
 * START and the whole sign-up block are on screen without scrolling down to
 * about 600px tall. At 1000px and taller every clamp is at its old value.
 */
const FIT_SHORT = `
@media (min-width: 901px) and (min-height: 501px) and (max-height: 1000px) {
  #menu .topbar { padding-top: 10px; padding-bottom: 10px; }
  #menu .sheet { padding-top: clamp(12px, 2.6vh, 30px); padding-bottom: 12px; }
  #menu .masthead { margin-bottom: clamp(10px, 2.4vh, 32px); align-items: center; }
  #menu .brandmark { height: clamp(50px, 9.6vh, 96px); }
  #menu h1 { font-size: clamp(22px, min(5.2vw, 4.5vh), 45px); }
  #menu h1 .l2 { margin-top: clamp(4px, 1vh, 10px); }
  /* the hard shadows shrink with the title (7px at its full 45px), or they smear into CHASE */
  #menu h1 .l1 { text-shadow: .155em .155em 0 #CAEE10; }
  #menu h1 .l2 { text-shadow: .155em .155em 0 #407A7D; }
  #menu .badge { margin-top: 0; }
  #menu .roster { margin-bottom: clamp(18px, 3.4vh, 56px); }
  /* the portraits take the height that is left over: their usual
     278:170 shape, capped by what the window has room for (a max-height on an
     aspect-ratio box would narrow it too, so the height is set from the
     card's own width in container units) */
  #menu .dog { container-type: inline-size; }
  #menu .dog .frame { aspect-ratio: auto; height: max(72px, min(calc(100cqw * 170 / 278), calc(100vh - 560px))); }
  #menu .dog .head { padding-top: 8px; padding-bottom: 8px; }
  #menu .dog .info { padding: 10px 12px 8px; }
  #menu .dog .breed { margin-bottom: 7px; }
  #menu .dog .blurb { margin-bottom: 8px; }
  #menu .stat { margin-bottom: 4px; }
  /* a narrower board below ~1190px wide, so the form keeps name, email and
     START on one row (it needs about 670px) */
  #menu .footer { gap: 20px 32px; grid-template-columns: clamp(200px, 100vw - 860px, 330px) minmax(0, 1fr); }
  #menu .board-tabs { margin-bottom: 10px; }
  #menu .board ol { line-height: 1.9; }
  #menu .collect { padding: clamp(12px, 2.2vh, 24px) 22px; }
  #menu .collect .inwrap input { height: clamp(48px, 7vh, 62px); padding-top: 0; padding-bottom: 0; }
  #menu .startcol .start { height: clamp(48px, 7vh, 62px); }
  #menu .row2 { margin-top: clamp(8px, 1.6vh, 18px); }
  #menu .consent-row { margin-top: 4px; }
  #menu .cnotice { margin-top: 6px; }
}
`;

/*
 * Touch: the small controls keep their look but get a 44px hit area, so a
 * finger can hit them on a phone or tablet.
 */
const FIT_TOUCH = `
@media (pointer: coarse) {
  #menu .tab, #menu .music-toggle { position: relative; }
  #menu .tab::after, #menu .music-toggle::after {
    content: ''; position: absolute; left: 50%; top: 50%; min-width: 100%; min-height: 100%;
    width: 44px; height: 44px; transform: translate(-50%, -50%);
  }
  /* the privacy link: taller padding cancelled by a negative margin, so nothing moves */
  #menu .cnotice a { padding: 15px 0; margin: -15px 0; }
}
`;

/* a tablet held upright has room for a bigger masthead than a phone */
const FIT_TABLET = `
@media (min-width: 601px) and (max-width: 900px) and (orientation: portrait) and (min-height: 700px) {
  #menu .brandmark { height: 64px; }
  #menu h1 { font-size: 27px; }
  #menu .badge { font-size: 10px; max-width: 150px; padding: 9px 10px; }
  #menu .sheet { padding-top: 22px; }
  #menu .masthead { margin-bottom: 22px; }
}
`;

/*
 * A window that is both narrow and short (about 900-1060 wide, under 660
 * tall, such as 960x600): the cards are too narrow for their text to fit in
 * the height left, so they go compact (name and portrait) and the picked
 * dog's breed, blurb and stats show once, in a strip under them, as on a
 * phone or a tablet held upright.
 */
const FIT_NARROW_SHORT = `
@media (min-width: 901px) and (max-width: 1060px) and (min-height: 501px) and (max-height: 660px) {
  #menu .dog .info { display: none; }
  #menu .dog .frame { height: max(96px, min(calc(100cqw * 170 / 278), calc(100vh - 470px))); }
  #menu .roster { margin-bottom: clamp(14px, 2.4vh, 24px); }
  #menu .picked-detail {
    display: grid; grid-template-columns: minmax(0, 1fr) auto; column-gap: 28px; align-items: center;
    background: ${PICKED_BG}; color: #ECEFF1; border: 3px solid ${LIME}; box-shadow: 6px 6px 0 ${SLATE};
    padding: 8px 14px; margin: 0 16px clamp(16px, 2.6vh, 24px) 0;
  }
  #menu .picked-detail .breed { grid-column: 1; font-size: 9px; line-height: 1.4; color: ${LIME}; margin-bottom: 5px; }
  #menu .picked-detail .blurb { grid-column: 1; font-size: 8px; line-height: 1.7; }
  #menu .picked-detail .pd-stats { grid-column: 2; grid-row: 1 / span 2; display: grid; grid-template-columns: auto auto; column-gap: 24px; row-gap: 5px; }
  #menu .picked-detail .stat { margin: 0; }
  #menu .picked-detail .stat .label { color: ${MIST}; }
  #menu .picked-detail .bar { border-color: #78909C; }
  #menu .picked-detail .bar.on { background: ${LIME}; border-color: ${PICKED_BG}; }
  #menu .picked-detail .pd-empty { font-size: 8px; line-height: 1.8; }
  /* the picked card keeps its star, not the word, as on the smallest phones */
  #menu .dog.picked .head .pk { display: none; }
  /* name, email and START stay on one row, with narrower name and START */
  #menu .collect .fields { grid-template-columns: 150px minmax(0, 1fr) 150px; }
  #menu .startcol { grid-column: auto; margin-top: 0; align-items: flex-end; }
  #menu .startcol .cap { text-align: right; }
}
`;


/*
 * Phones.
 * Held sideways (a window under 500px tall): two columns, the compact cards,
 * the picked dog and the board on the left, the form with START last on the
 * right, under a slim one-line masthead, so the whole screen fits.
 * Held upright: a little less air between blocks, so START is above the fold
 * on a 390x844 phone; on a short one (iPhone SE, 667px) the portraits are
 * shorter and the masthead one line as well.
 */
const FIT_PHONE = `
@media (orientation: landscape) and (max-height: 500px) {
  #menu .topbar { padding: 6px 16px; }
  #menu .sheet {
    max-width: none; padding: 8px max(16px, env(safe-area-inset-right)) 10px max(16px, env(safe-area-inset-left));
    display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr); column-gap: 18px;
    grid-template-areas: "mast form" "roster form" "detail form" "board form"; align-items: start;
  }
  #menu .masthead { grid-area: mast; margin-bottom: 8px; }
  #menu .brandmark { height: 26px; }
  #menu h1 { font-size: 13px; display: flex; gap: 8px; align-items: baseline; }
  #menu h1 .l2 { margin-top: 0; }
  #menu h1 .l1, #menu h1 .l2 { text-shadow: 2px 2px 0 ${LIME}; }
  #menu h1 .l2 { text-shadow: 2px 2px 0 #407A7D; }
  #menu .badge { font-size: 7px; padding: 5px 6px; max-width: none; white-space: nowrap; }
  #menu .roster { grid-area: roster; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; margin-bottom: 10px; padding-right: 4px; }
  #menu .dog .head { padding: 4px 5px; font-size: 7px; }
  #menu .dog .head .no { font-size: 7px; }
  #menu .dog.picked .head .pk { display: none; }
  #menu .dog .frame { aspect-ratio: 278 / 150; }
  #menu .dog { box-shadow: 3px 3px 0 ${INK}; }
  #menu .dog.picked { box-shadow: 3px 3px 0 ${LIME}, 6px 6px 0 ${SLATE}; }
  #menu .picked-detail { grid-area: detail; margin: 0 4px 10px 0; padding: 6px 8px 2px; }
  #menu .picked-detail .breed { font-size: 7px; margin-bottom: 4px; }
  #menu .picked-detail .blurb { font-size: 7px; line-height: 1.5; margin-bottom: 4px; }
  #menu .picked-detail .stat { margin-bottom: 3px; }
  #menu .footer { display: contents; }
  #menu .board { grid-area: board; }
  #menu .board-tabs { margin-bottom: 6px; gap: 8px; }
  #menu .tab { font-size: 7px; padding: 5px 8px; }
  #menu .board ol { font-size: 7px; line-height: 1.7; }
  #menu .collect { grid-area: form; padding: 10px 12px 12px; box-shadow: 4px 4px 0 ${INK}; }
  #menu .collect .cfld + .cfld { margin-top: 8px; }
  #menu .clbl { font-size: 7px; height: 12px; }
  #menu .cfld { gap: 5px; }
  #menu .collect .inwrap input { height: 44px; padding: 0 10px; box-shadow: 3px 3px 0 ${INK}; }
  #menu .row2 { margin-top: 6px; }
  #menu .st { height: 14px; font-size: 7px; }
  #menu .consent-row { margin-top: 2px; font-size: 7px; }
  #menu .cnotice { font-size: 7px; line-height: 12px; margin-top: 4px; }
  #menu .startcol { margin-top: 8px; gap: 4px; }
  #menu .startcol .cap { font-size: 7px; }
  #menu .startcol .start { height: 44px; font-size: 13px; box-shadow: 4px 4px 0 ${INK}; }
}
@media (max-width: 600px) and (orientation: portrait) {
  #menu .sheet { padding-top: 10px; }
  #menu .masthead { margin-bottom: 10px; }
  #menu .roster { margin-bottom: 12px; gap: 10px 14px; }
  #menu .picked-detail { margin-bottom: 12px; }
}
@media (orientation: landscape) and (max-height: 500px) and (max-width: 720px) {
  #menu .dog:not(.picked) .head .no { display: none; }
}
/* a short phone held upright (iPhone SE, 667px): shorter portraits, so START still makes the fold */
@media (max-width: 600px) and (orientation: portrait) and (max-height: 740px) {
  #menu .topbar { padding-top: 6px; padding-bottom: 6px; }
  #menu .sheet { padding-top: 8px; }
  #menu .masthead { margin-bottom: 8px; }
  #menu .startcol { margin-top: 6px; gap: 4px; }
  #menu .cnotice { margin-top: 6px; }
  #menu .brandmark { height: 28px; }
  #menu h1 { font-size: 14px; display: flex; gap: 8px; align-items: baseline; }
  #menu h1 .l2 { margin-top: 0; }
  #menu h1 .l1 { text-shadow: .14em .14em 0 ${LIME}; }
  #menu h1 .l2 { text-shadow: .14em .14em 0 #407A7D; }
  #menu .badge { font-size: 7px; padding: 5px 6px; max-width: none; }
  #menu .st { height: 16px; }
  #menu .consent-row { margin-top: 6px; }
  #menu .dog .frame { aspect-ratio: 278 / 66; }
  #menu .dog .head { padding: 5px 8px; }
  #menu .picked-detail { padding: 8px 10px 4px; }
  #menu .picked-detail .blurb { font-size: 7px; line-height: 1.5; margin-bottom: 5px; }
  #menu .picked-detail .breed { margin-bottom: 4px; }
  #menu .roster { gap: 8px 12px; margin-bottom: 10px; }
  #menu .collect { padding: 10px 14px 12px; }
  #menu .collect .inwrap input { height: 44px; }
  #menu .collect .cfld + .cfld { margin-top: 10px; }
  #menu .row2 { margin-top: 6px; }
  #menu .startcol .start { height: 48px; }
}
`;

const FIT_STYLES = FIT_SHORT + FIT_NARROW_SHORT + FIT_TABLET + FIT_TOUCH + FIT_PHONE;

const STYLES = `
@font-face {
  font-family: 'Press Start 2P';
  src: url(${arcadeFont}) format('woff2');
  /* swap, not block: the start screen is prerendered into the page
     (tools/prerender_menu.mjs) and its text is the LCP element, so under
     block nothing counts as painted until the font lands and the early
     paint is given away. The face is preloaded, so the fallback shows only
     while the font is in flight. */
  font-display: swap;
}
#menu {
  position: fixed; inset: 0; overflow-y: auto; z-index: 10; display: none;
  background: linear-gradient(180deg, #E2F2F2 0%, #A1CBC2 100%);
  color: ${INK}; font-family: 'Press Start 2P', monospace;
  /* clear of a notch: the page runs edge to edge (viewport-fit=cover); 0 elsewhere */
  padding-left: env(safe-area-inset-left, 0px); padding-right: env(safe-area-inset-right, 0px);
}
#menu.is-open { display: block; }
#menu *, #menu *::before, #menu *::after { box-sizing: border-box; }

#menu .topbar {
  background: ${INK}; color: ${PAPER}; display: grid; grid-template-columns: 1fr auto 1fr;
  align-items: center; gap: 12px; padding: 12px 34px; font-size: 9px; line-height: 1;
}
#menu .topbar .mid { color: ${LIME}; text-align: center; }
#menu .topbar span { white-space: nowrap; }
#menu .topbar .right { display: flex; align-items: center; justify-content: flex-end; gap: 18px; }
#menu .music-toggle {
  font: inherit; font-size: 9px; line-height: 1; color: ${PAPER}; background: transparent;
  border: 2px solid ${PAPER}; padding: 5px 8px; cursor: pointer; white-space: nowrap;
}
#menu .music-toggle:hover { color: ${LIME}; border-color: ${LIME}; }
#menu .music-toggle:focus-visible { outline: 2px solid ${LIME}; outline-offset: 2px; }
#menu .music-toggle[aria-pressed="true"] { opacity: .6; }
@media (max-width: 700px) { #menu .topbar .mid { display: none; } }

#menu .sheet { max-width: 1184px; margin: 0 auto; padding: 30px 24px 48px; }
#menu .masthead {
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: 24px; flex-wrap: wrap; margin-bottom: 32px;
}
#menu .brand { display: flex; align-items: center; gap: clamp(14px, 2vw, 26px); }
/* sized with the title: roughly the height of its two lines at every width */
#menu .brandmark { height: clamp(64px, 11.1vw, 96px); width: auto; display: block; flex: none; }
#menu h1 {
  margin: 0; font-size: clamp(30px, 5.2vw, 45px); line-height: 1.05;
  font-weight: 700; letter-spacing: 1px;
}
#menu h1 .l1 { display: block; text-shadow: 7px 7px 0 #CAEE10; }
#menu h1 .l2 { display: block; text-shadow: 7px 7px 0 #407A7D; margin-top: 10px; }
#menu .badge {
  background: ${LIME}; border: 3px solid ${INK}; color: ${INK};
  padding: 12px 20px; font-size: 13px; line-height: 1; white-space: nowrap;
  transform: rotate(3deg); transform-origin: top left; margin-top: 40px;
}

#menu .roster { display: grid; grid-template-columns: repeat(4, 1fr); gap: 30px; margin-bottom: 56px; padding-right: 16px; }
#menu .dog {
  border: 3px solid ${INK}; background: ${PAPER}; cursor: pointer; text-align: left;
  padding: 0; font: inherit; color: inherit; display: block; width: 100%;
  opacity: .78; box-shadow: 8px 8px 0 ${INK};
  transform: rotate(var(--tilt, 0deg));
  transition: opacity .15s ease, transform .2s ease, box-shadow .2s ease;
}
/* hover swings the card the other way and lifts it: up-left, shadow growing,
   the way a hard-shadow card comes off the page */
#menu .dog:not(.picked):hover,
#menu .dog:not(.picked):focus-visible {
  opacity: 1;
  transform: rotate(var(--tilt-hover, 0deg)) translate(-2px, -3px);
  box-shadow: 11px 11px 0 ${INK};
}
@media (prefers-reduced-motion: reduce) {
  #menu .dog { transition: none; }
  #menu .dog:not(.picked):hover,
  #menu .dog:not(.picked):focus-visible { transform: rotate(var(--tilt, 0deg)); }
}
#menu .dog:focus-visible { outline: 4px solid ${INK}; outline-offset: 3px; }
#menu .dog .head {
  display: flex; justify-content: space-between; align-items: center; gap: 8px;
  padding: 10px 14px; font-size: 13px; font-weight: 700; color: ${PAPER};
}
#menu .dog .head .no { font-size: 13px; font-weight: normal; }
#menu .dog.picked .head .no { font-size: 9px; }
#menu .dog .frame {
  display: flex; align-items: flex-end; justify-content: center;
  aspect-ratio: 278 / 170; overflow: hidden;
}
/* sits on the bottom edge of its block, as the stills did */
#menu .dog .portrait {
  height: 94%; image-rendering: pixelated; background-repeat: no-repeat;
  background-size: 300% 100%;
  animation: portrait-idle ${LOOP_SECONDS}s step-end infinite;
}
/* idle, a 0.2s blink, idle, the tongue out for 0.45s, idle */
@keyframes portrait-idle {
  0%   { background-position: 0% 0; }
  38%  { background-position: 50% 0; }
  42%  { background-position: 0% 0; }
  62%  { background-position: 100% 0; }
  72%  { background-position: 0% 0; }
}
@media (prefers-reduced-motion: reduce) {
  #menu .dog .portrait { animation: none; }
}
#menu .dog .info { padding: 12px; }
#menu .dog .breed { font-size: 8px; line-height: 1; margin-bottom: 10px; }
#menu .dog .blurb { font-size: 8px; line-height: 1.9; min-height: 30px; margin-bottom: 14px; }
#menu .stat { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 7px; }
#menu .stat .label { font-size: 7px; line-height: 1; color: ${INK}; }
#menu .bars { display: flex; gap: 3px; flex: none; }
#menu .bar { width: 13px; height: 10px; border: 1px solid ${INK}; background: transparent; }
/* On first load each stat row pops in, top to bottom, then fills its bars one
   segment at a time, like the stats are loading. --d is set per row and per
   bar in roster(); the class comes off at the first pick so a redraw doesn't
   replay it. Animations outrank inline styles, so the empty frame wins over
   a bar's inline fill until its delay runs out. */
#menu .roster.boot .stat { animation: stat-in .01s step-end backwards; animation-delay: var(--d); }
#menu .roster.boot .bar.on { animation: bar-fill .01s step-end backwards; animation-delay: var(--d); }
@keyframes stat-in { from { opacity: 0; } }
@keyframes bar-fill { from { background: transparent; } }
@media (prefers-reduced-motion: reduce) {
  #menu .roster.boot .stat, #menu .roster.boot .bar.on { animation: none; }
}

#menu .dog.picked {
  background: ${PICKED_BG}; border: 4px solid ${LIME}; opacity: 1; transform: none;
  box-shadow: 10px 10px 0 ${LIME}, 16px 16px 0 ${SLATE};
}
#menu .dog.picked .frame { background: ${SLATE}; }
#menu .dog.picked .head { background: ${LIME}; color: ${PICKED_BG}; }
#menu .dog.picked .breed { color: ${LIME}; }
#menu .dog.picked .blurb { color: #ECEFF1; }
#menu .dog.picked .stat .label { color: ${MIST}; }
#menu .dog.picked .bar { border-color: #78909C; }
#menu .dog.picked .bar.on { background: ${LIME}; border-color: ${PICKED_BG}; }

#menu .footer { display: grid; grid-template-columns: 330px minmax(0, 1fr); gap: 30px 40px; align-items: start; }
#menu .board { align-self: start; min-width: 0; }
#menu .board-tabs { display: flex; gap: 12px; margin: 0 0 16px; }
#menu .tab {
  font: inherit; font-size: 9px; color: ${INK}; background: transparent; cursor: pointer;
  border: 3px solid ${INK}; padding: 8px 12px; opacity: .6;
}
#menu .tab:hover { opacity: 1; }
#menu .tab[aria-selected="true"] { background: ${LIME}; opacity: 1; box-shadow: 4px 4px 0 ${INK}; }
#menu .tab:focus-visible { outline: 2px solid ${INK}; outline-offset: 3px; }
#menu .board li .chip { width: 10px; height: 10px; border: 1px solid ${INK}; flex: none; align-self: center; }
#menu .board ol { list-style: none; margin: 0; padding: 0; font-size: 9px; line-height: 2.1; }
#menu .board li { display: flex; gap: 10px; }
#menu .board li .res { flex: none; font-size: 7px; opacity: .75; align-self: center; }
#menu .board li .res.win { opacity: 1; font-size: 9px; }
#menu .board li .nm { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
#menu .board .empty { font-size: 9px; line-height: 1.9; opacity: .7; }


#menu .start {
  font: inherit; font-size: 16px; background: ${LIME}; border: 3px solid ${INK};
  color: ${INK}; padding: 20px 56px; cursor: pointer; white-space: nowrap;
  box-shadow: 8px 8px 0 ${INK};
}
#menu .start:active:not(:disabled) { transform: translate(4px, 4px); box-shadow: 4px 4px 0 ${INK}; }
#menu .start:disabled { opacity: .45; cursor: not-allowed; }
${START_FORM_STYLES}
#menu .picked-detail { display: none; }
@media (max-width: 900px) { #menu .roster { grid-template-columns: repeat(2, 1fr); } #menu .footer { grid-template-columns: 1fr; } }
/* A phone held upright, and a tablet held upright (up to 900px wide): the four
   dogs as a 2x2 grid of compact cards (name and portrait), the picked dog's
   breed, blurb and stats shown once under them, then the form and START, then
   the board. Picking and starting fit on about one screen instead of four
   full-width cards stacked above the form. */
@media (max-width: 600px), (max-width: 900px) and (orientation: portrait) and (min-height: 700px), (orientation: landscape) and (max-height: 500px) {
  #menu .topbar { padding: 10px 16px; }
  #menu .sheet { padding: 14px 16px 40px; }
  #menu .masthead { flex-wrap: nowrap; align-items: center; gap: 12px; margin-bottom: 14px; }
  #menu .brand { gap: 10px; }
  #menu .brandmark { height: 44px; }
  #menu h1 { font-size: 19px; }
  #menu h1 .l1 { text-shadow: 3px 3px 0 #CAEE10; }
  #menu h1 .l2 { text-shadow: 3px 3px 0 #407A7D; margin-top: 5px; }
  #menu .badge {
    font-size: 8px; line-height: 1.5; padding: 7px 8px; margin-top: 0; white-space: normal;
    text-align: center; max-width: 104px; border-width: 2px;
  }
  #menu .roster { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 14px; margin-bottom: 16px; padding-right: 6px; }
  #menu .dog { box-shadow: 5px 5px 0 ${INK}; }
  #menu .dog:not(.picked):hover, #menu .dog:not(.picked):focus-visible { box-shadow: 7px 7px 0 ${INK}; }
  #menu .dog.picked { box-shadow: 5px 5px 0 ${LIME}, 9px 9px 0 ${SLATE}; }
  #menu .dog .head { padding: 7px 8px; font-size: 10px; }
  #menu .dog .head .no { font-size: 9px; }
  #menu .dog.picked .head .no { font-size: 7px; white-space: nowrap; }
  #menu .dog .frame { aspect-ratio: 278 / 112; }
  #menu .dog .info { display: none; }
  #menu .picked-detail {
    display: block; background: ${PICKED_BG}; color: #ECEFF1; border: 3px solid ${LIME};
    box-shadow: 5px 5px 0 ${SLATE}; padding: 10px 12px 6px; margin: 0 6px 22px 0;
  }
  #menu .picked-detail .breed { font-size: 8px; line-height: 1.4; color: ${LIME}; margin-bottom: 8px; }
  #menu .picked-detail .blurb { font-size: 8px; line-height: 1.8; margin-bottom: 10px; }
  #menu .picked-detail .pd-stats { display: grid; grid-template-columns: 1fr 1fr; column-gap: 14px; }
  #menu .picked-detail .stat { margin-bottom: 6px; }
  #menu .picked-detail .stat .label { color: ${MIST}; }
  #menu .picked-detail .bar { width: 10px; height: 9px; border-color: #78909C; }
  #menu .picked-detail .bar.on { background: ${LIME}; border-color: ${PICKED_BG}; }
  #menu .picked-detail .pd-empty { font-size: 8px; line-height: 1.8; }
  #menu .footer { gap: 28px; }
  #menu .collect { order: -1; padding: 14px 16px 16px; box-shadow: 6px 6px 0 ${INK}; }
  #menu .consent-row { height: auto; }
  #menu .row2 { margin-top: 12px; }
  #menu .collect .inwrap input { height: 48px; padding: 14px 14px; }
  #menu .startcol .start { height: 54px; }
}
/* the smallest phones: the picked card's header keeps its star, not the word */
@media (max-width: 380px) { #menu .dog.picked .head .pk { display: none; } #menu .dog.picked .head .no { font-size: 10px; } }
${FIT_STYLES}
${NUDGE_STYLES}
`;

class Menu {
  constructor() {
    this.root = null;
    this.selected = null;
    this.playerName = '';
    this.form = null;
  }

  /**
   * remembered
   * @returns {{name?: string, email?: string, dog?: string}} the last details
   *   used, so a replay doesn't retype. The name and dog are in localStorage,
   *   so they survive a visit; the email is in sessionStorage, so it goes when
   *   the tab closes and the next person at a shared or event-booth machine
   *   doesn't see it.
   */
  remembered() {
    let saved = {};
    try {
      saved = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      saved = {};
    }
    if (saved.email) {
      // left by an earlier build, which kept the email in localStorage
      delete saved.email;
      this.store('localStorage', STORAGE_KEY, saved);
    }
    try {
      saved.email = sessionStorage.getItem(EMAIL_KEY) || '';
    } catch (e) {
      saved.email = '';
    }
    return saved;
  }

  /**
   * remember
   * Name, email and last dog only: the consent box is never stored.
   * @param {{name?: string, email?: string, dog?: string}} player
   */
  remember(player) {
    const {name = '', email = '', dog = null} = player;
    this.store('localStorage', STORAGE_KEY, {name, dog});
    this.store('sessionStorage', EMAIL_KEY, email);
  }

  /**
   * store
   * @param {string} storage 'localStorage' or 'sessionStorage', looked up
   *   inside the try because even reading the property can throw
   * @param {string} key
   * @param {Object|string} value objects are stored as JSON
   * @private
   */
  store(storage, key, value) {
    try {
      window[storage].setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
    } catch (e) {
      // private browsing, or storage is full -- the game still plays
    }
  }

  /**
   * mount
   * Builds the overlay once and keeps it around, hidden, between screens.
   * @private
   */
  mount() {
    if (this.root) {
      return;
    }
    // A start screen prerendered into the page (tools/prerender_menu.mjs
    // writes it, and its styles, into dist/index.html) has already painted
    // before this script ran. Adopt it: building the same markup again and
    // injecting the same styles would cost a second layout for no change.
    const prerendered = document.getElementById('menu');
    if (prerendered && prerendered.firstElementChild) {
      this.root = prerendered;
      this.prerendered = true;
      return;
    }

    const style = document.createElement('style');
    style.textContent = STYLES;
    document.head.appendChild(style);

    this.root = document.createElement('div');
    this.root.id = 'menu';
    document.body.appendChild(this.root);
  }

  /**
   * adopt
   * Brings a prerendered start screen up to date with this player. The
   * markup was written at build time, so it carries no remembered name or
   * email; the dog is applied by pickedDog() as usual.
   * @param {Object} remembered
   * @private
   */
  adopt(remembered) {
    this.prerendered = false;
    const name = this.root.querySelector('#player-name');
    const email = this.root.querySelector('#player-email');
    if (name) {
      name.value = remembered.name || '';
    }
    if (email) {
      email.value = remembered.email || '';
    }
  }

  /**
   * bars
   * The five-block meter from the design: filled blocks up to the value.
   * @param {Number} value 1-5
   * @param {String} accent the dog's colour, used for the filled blocks
   * @param {Boolean} picked whether this card is the chosen one
   * @returns {String}
   * @private
   */
  bars(value, accent, picked, delay) {
    let row = '';
    for (let i = 1; i <= 5; i++) {
      const on = i <= value;
      const fill = on && !picked ? `background:${accent};border-color:${INK};` : '';
      const style = on ? ` style="${fill}--d:${delay + i * STAT_TIMING.bar}ms"` : '';
      row += `<span class="bar${on ? ' on' : ''}"${style}></span>`;
    }
    return `<span class="bars">${row}</span>`;
  }

  /**
   * show
   * Opens the home screen: name, the four dogs, and the leaderboard. The
   * name, email and dog from last time are filled in.
   * @param {Object} [opts]
   * @param {Function} [opts.onDog] called with the dog's id whenever one is
   *   chosen, including the one remembered from last time (main.js starts
   *   downloading that dog's art)
   * @param {Function} [opts.cover] called with the player on START, before the
   *   start screen goes; returns a Promise that resolves once something covers
   *   it (main.js: the loading screen fading in), so the two crossfade
   * @returns {Promise<Object>} resolves with {name, dog, email, consent, token}
   *   once the run has started; token is null when the server was unreachable
   */
  show({onDog, cover} = {}) {
    this.mount();
    const remembered = this.remembered();
    this.playerName = remembered.name || '';
    this.playerEmail = remembered.email || '';
    this.selected = remembered.dog || null;
    this.onDog = onDog || null;
    this.cover = cover || null;
    this.form = new StartForm({leaderboard});

    if (this.prerendered) {
      this.adopt(remembered);
    } else {
      this.root.innerHTML = this.markup();
    }
    this.root.classList.add('is-open');
    music.play();
    sfx.load();
    this.fillBoard(true);
    if (this.stopBoardRefresh) {
      this.stopBoardRefresh();
    }
    this.stopBoardRefresh = leaderboard.onLanded(() => {
      if (this.root && this.root.classList.contains('is-open')) {
        this.fillBoard(false);
      }
    });

    return new Promise((resolve) => {
      this.bind(resolve);
    });
  }

  /**
   * skip
   * Takes the prerendered start screen down for a path that never shows one
   * -- main.js' ?dog= shortcut, which goes straight to a game. The shell is
   * in the page before any script runs (tools/prerender_menu.mjs), so
   * without this it stays over the canvas and the end panel for the whole
   * run. Does nothing when the page was not prerendered.
   */
  skip() {
    const prerendered = document.getElementById('menu');
    if (prerendered) {
      prerendered.classList.remove('is-open');
    }
  }

  hide() {
    if (this.stopBoardRefresh) {
      this.stopBoardRefresh();
      this.stopBoardRefresh = null;
    }
    if (this.root) {
      this.root.classList.remove('is-open');
    }
    // the theme carries on into the game, fading down under it
    music.setLevel('game');
  }

  /**
   * leave
   * Hides the start screen on START: at once, or once show()'s cover has
   * faded in over it, so there is never a blank frame between the two.
   * @param {Object} player
   * @private
   */
  leave(player) {
    let covered = null;
    try {
      covered = this.cover ? this.cover(player) : null;
    } catch (e) {
      covered = null;
    }
    if (covered && typeof covered.then === 'function') {
      covered.then(() => this.hide(), () => this.hide());
    } else {
      this.hide();
    }
  }

  /**
   * markup
   * @returns {String}
   * @private
   */
  markup() {
    return `
      <div class="topbar">
        <span class="tl">${nudgeMarkup()}</span>
        <span class="mid">1 PLAYER \u00b7 4 DOGS \u00b7 6 LEVELS</span>
        <span class="right">
          <button class="music-toggle" type="button" aria-label="Mute music"
                  aria-pressed="${music.muted}">${music.muted ? '\u266a OFF' : '\u266a ON'}</button>
        </span>
      </div>
      <div class="sheet">
        <div class="masthead">
          <div class="brand">
            <img class="brandmark" src="${brandmark}" alt="Weel">
            <h1><span class="l1">RECEIPT</span><span class="l2">CHASE</span></h1>
          </div>
          <div class="badge">PICK YOUR CHASER</div>
        </div>
        <div class="roster boot">${this.roster()}</div>
        <div class="picked-detail" aria-live="polite">${this.pickedDetail()}</div>
        <div class="footer">
          <div class="board">
            <div class="board-tabs" role="tablist" aria-label="Leaderboards">
              <button type="button" class="tab" role="tab" id="tab-users" data-board="users"
                      aria-controls="board-users" aria-selected="true">USERS</button>
              <button type="button" class="tab" role="tab" id="tab-dogs" data-board="dogs"
                      aria-controls="board-dogs" aria-selected="false" tabindex="-1">DOGS</button>
            </div>
            <div class="board-body" id="board-users" role="tabpanel" aria-labelledby="tab-users">
              <div class="empty">Loading&hellip;</div>
            </div>
            <div class="board-body" id="board-dogs" role="tabpanel" aria-labelledby="tab-dogs" hidden>
              <div class="empty">Loading&hellip;</div>
            </div>
          </div>
          ${this.form.markup({name: this.playerName, email: this.playerEmail})}
        </div>
      </div>
    `;
  }

  /**
   * roster
   * The four cards. Redrawn whole when the pick changes, because the picked
   * card inverts its colours rather than just gaining an outline.
   * @returns {String}
   * @private
   */
  roster() {
    return dogData.dogs.map((dog, index) => {
      const picked = dog.id === this.selected;
      const headStyle = picked ? '' : ` style="background:${dog.accent}"`;
      // tilts from the frame: alternating a degree either way, the pick sits flat
      const tilt = index % 2 === 0 ? -1 : 1;
      // resting tilt and hover tilt as variables, so :hover can override them
      const cardStyle = picked ? '' : ` style="--tilt:${tilt}deg;--tilt-hover:${-tilt * 1.2}deg"`;
      // the portrait block is the dog's colour at 18%, as in the design
      const frameStyle = picked ? '' : ` style="background:${dog.accent}2E"`;
      const stats = Object.keys(dogData.statLabels).map((key, row) => {
        const delay = STAT_TIMING.start + index * STAT_TIMING.card + row * STAT_TIMING.row;
        return `
          <div class="stat" style="--d:${delay}ms">
            <span class="label">${dogData.statLabels[key]}</span>
            ${this.bars(dog.stats[key], dog.accent, picked, delay)}
          </div>
        `;
      }).join('');

      return `
        <button type="button" class="dog${picked ? ' picked' : ''}" data-dog="${dog.id}" aria-pressed="${picked}"${cardStyle}>
          <div class="head"${headStyle}>
            <span>${dog.name}</span>
            <span class="no">${picked ? '\u2605<span class="pk"> PICKED</span>' : '0' + (index + 1)}</span>
          </div>
          <div class="frame"${frameStyle}>
            <div class="portrait" role="img" aria-label="${dog.name}, ${dog.breed}"
                 style="background-image:url(${PORTRAIT_STRIPS[dog.id]});aspect-ratio:${portraitFrames[dog.id].width}/${portraitFrames[dog.id].height};animation-delay:-${PORTRAIT_OFFSET[dog.id]}s"></div>
          </div>
          <div class="info">
            <div class="breed">${dog.breed}</div>
            <div class="blurb">${dog.blurb}</div>
            ${stats}
          </div>
        </button>
      `;
    }).join('');
  }

  /**
   * pickedDetail
   * The picked dog's breed, blurb and stats, shown once under the compact
   * cards on a phone held upright (hidden otherwise: the full cards carry them).
   * @returns {String}
   * @private
   */
  pickedDetail() {
    const dog = dogData.dogs.filter((d) => d.id === this.selected)[0];
    if (!dog) {
      return '<div class="pd-empty">Tap a dog to see how it plays.</div>';
    }
    const stats = Object.keys(dogData.statLabels).map((key) => `
      <div class="stat">
        <span class="label">${dogData.statLabels[key]}</span>
        ${this.bars(dog.stats[key], dog.accent, true, 0)}
      </div>`).join('');
    return `
      <div class="breed">${dog.name} \u00b7 ${dog.breed}</div>
      <div class="blurb">${dog.blurb}</div>
      <div class="pd-stats">${stats}</div>`;
  }

  /**
   * bindTabs
   * Users / Dogs switch over the board. Arrow keys move between the tabs, as
   * a tab list is expected to.
   * @private
   */
  bindTabs() {
    const tabs = Array.prototype.slice.call(this.root.querySelectorAll('.tab'));
    const select = (tab) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        this.root.querySelector(`#${t.getAttribute('aria-controls')}`).hidden = !on;
      });
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', (event) => {
        const step = {ArrowRight: 1, ArrowLeft: -1}[event.key];
        if (step) {
          const next = tabs[(i + step + tabs.length) % tabs.length];
          select(next);
          next.focus();
        }
      });
    });
  }

  /**
   * bindSounds
   * Hover and click sounds, delegated from the menu root so they survive the
   * roster being redrawn on every pick. The root is refilled on each show(),
   * so it's only bound once.
   * @private
   */
  bindSounds() {
    if (this.soundsBound) {
      return;
    }
    this.soundsBound = true;
    const TARGETS = '.dog, .start, .music-toggle, .tab';

    this.root.addEventListener('mouseover', (event) => {
      const target = event.target.closest(TARGETS);
      // moving between a card's children fires mouseover too; only entering counts
      if (!target || target.disabled || target.contains(event.relatedTarget)) {
        return;
      }
      sfx.hover();
    });
    this.root.addEventListener('click', (event) => {
      const target = event.target.closest(TARGETS);
      if (!target) {
        return;
      }
      if (target.classList.contains('dog')) {
        sfx.pick();
      } else {
        sfx.click();
      }
    });
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * bind
   * @param {Function} resolve called with the chosen player once Start is hit
   * @private
   */
  bind(resolve) {
    bindNudge(this.root);
    const chosenName = () => {
      const chosen = dogData.dogs.filter((d) => d.id === this.selected)[0];
      return chosen ? chosen.name : '';
    };

    const toggle = this.root.querySelector('.music-toggle');
    toggle.addEventListener('click', () => {
      const muted = music.toggle();
      toggle.setAttribute('aria-pressed', String(muted));
      toggle.textContent = muted ? '\u266a OFF' : '\u266a ON';
    });

    const bindCards = () => {
      const roster = this.root.querySelector('.roster');
      Array.prototype.forEach.call(roster.querySelectorAll('.dog'), (card) => {
        card.addEventListener('click', () => {
          this.selected = card.getAttribute('data-dog');
          roster.classList.remove('boot');
          roster.innerHTML = this.roster();
          this.root.querySelector('.picked-detail').innerHTML = this.pickedDetail();
          bindCards();
          this.form.setDog(this.selected, chosenName());
          this.pickedDog();
        });
      });
    };
    bindCards();
    // the dog remembered from last time is picked already
    this.pickedDog();

    this.bindTabs();
    this.bindSounds();

    // On a phone, START also asks for fullscreen (and main.js then locks the
    // screen to landscape where the browser allows): the browser only grants
    // it from the tap itself, before the form's server round trip. START is
    // disabled until the form is ready, so a disabled tap asks for nothing.
    this.root.querySelector('.start').addEventListener('click', requestGameFullscreen);

    this.form.bind(this.root, {
      onChange: ({name, email}) => {
        this.playerName = name;
        this.playerEmail = email;
      },
      onStart: ({name, email, consent, token}) => {
        const player = {name, email, dog: this.selected};
        this.remember(player);
        this.leave(player);
        resolve(Object.assign({consent, token}, player));
      }
    });
    // not focused on open: the YOUR NAME placeholder clears on focus, so
    // focusing here would hide it before anyone had read it
    this.form.setDog(this.selected, chosenName());
  }

  /**
   * pickedDog
   * Tells show()'s onDog about the dog now picked. A listener that throws
   * must not break the start screen.
   * @private
   */
  pickedDog() {
    // a dog remembered from an older roster has no art to fetch
    if (!this.onDog || !dogData.dogs.some((d) => d.id === this.selected)) {
      return;
    }
    try {
      this.onDog(this.selected);
    } catch (e) {
      // only a head start on the art; the game loads it anyway
    }
  }

  /**
   * fillBoard
   * Loads the top five. A finished run is submitted and reported by the end
   * panel, not here; one an earlier page couldn't send goes first, on load
   * (Leaderboard.watchOutbox), and the board waits a moment for it.
   * @param {Boolean} [firstFill] the first fill, which waits for that
   * @private
   */
  async fillBoard(firstFill) {
    const body = this.root.querySelector('#board-users');

    if (!leaderboard.configured) {
      this.fillDogBoard();
      body.innerHTML = '<div class="empty">Leaderboard not connected yet.</div>';
      return;
    }
    if (firstFill) {
      await leaderboard.whenLoadPassDone(OUTBOX_WAIT_MS);
    }
    this.fillDogBoard();

    const rows = await leaderboard.top();
    if (!rows.length) {
      body.innerHTML = '<div class="empty">No scores yet. Be first.</div>';
      return;
    }

    body.innerHTML = `
      <ol>
        ${rows.map((row, i) => {
          const res = boardResult(row);
          return `
          <li>
            <span>${i + 1}</span>
            <span class="nm">${this.escape(row.name)}</span>
            <span class="res${row.won ? ' win' : ''}"${res.title ? ` title="${res.title}"` : ''}>${res.label}</span>
            <span>${row.score.toLocaleString()}</span>
          </li>
        `;
        }).join('')}
      </ol>
    `;
  }

  /**
   * fillDogBoard
   * All four dogs, ranked by every point they've earned for everyone. A dog
   * nobody has played yet still shows, at 0.
   * @private
   */
  async fillDogBoard() {
    const body = this.root.querySelector('#board-dogs');
    if (!leaderboard.configured) {
      body.innerHTML = '<div class="empty">Leaderboard not connected yet.</div>';
      return;
    }

    const earned = {};
    (await leaderboard.dogTotals()).forEach((row) => {
      earned[row.dog] = row.points;
    });
    // most points first; a tie keeps the roster's order
    const rows = dogData.dogs
      .map((dog, order) => ({dog: dog, order: order, points: earned[dog.id] || 0}))
      .sort((a, b) => b.points - a.points || a.order - b.order);

    body.innerHTML = `
      <ol>
        ${rows.map((row, i) => `
          <li>
            <span>${i + 1}</span>
            <span class="chip" style="background:${row.dog.accent}" aria-hidden="true"></span>
            <span class="nm">${row.dog.name}</span>
            <span title="${row.points.toLocaleString()} points">${compactPoints(row.points)}</span>
          </li>
        `).join('')}
      </ol>
    `;
  }
}

/**
 * compactPoints
 * Totals past a thousand as 2.5k, 2.14m and so on: three significant figures,
 * rounded down so a total never reads higher than it is. Done in whole
 * numbers, because 2.3 * 100 in floating point comes out at 229.99...
 * @param {Number} n
 * @returns {String}
 */
function compactPoints(n) {
  const units = ['k', 'm', 'b', 't'];
  if (n < 1000) {
    return String(n);
  }
  let unit = 0;
  let scale = 1000;
  while (n >= scale * 1000 && unit < units.length - 1) {
    scale *= 1000;
    unit++;
  }
  const whole = Math.floor(n / scale);
  const decimals = whole >= 100 ? 0 : whole >= 10 ? 1 : 2;
  const shift = Math.pow(10, decimals);
  let shown = (Math.floor(n * shift / scale) / shift).toFixed(decimals);
  if (decimals) {
    shown = shown.replace(/\.?0+$/, '');
  }
  return shown + units[unit];
}

export default new Menu();
